/**
 * ClipReadyPanel — shows the auto-detected clip preview after AI
 * detection completes. Three actions:
 *   • Use this clip       → grade auto-detected segments
 *   • Adjust clip         → enter MM:SS start/end and grade only that
 *   • Use original range  → grade the full transcript with focus bias
 *
 * Owner directive: instructor-controlled, fast, no complex editing.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Play, Wand2, RotateCcw, Loader2 } from "lucide-react";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;

const CONFIDENCE_TONE = {
  high: "bg-emerald-500/15 border-emerald-500/30 text-emerald-300",
  medium: "bg-amber-500/15 border-amber-500/30 text-amber-300",
  low: "bg-rose-500/15 border-rose-500/30 text-rose-300",
};

function fmtMmss(s) {
  const total = Math.max(0, Math.round(s || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

function mmssToSeconds(s) {
  if (!s) return null;
  const parts = String(s).split(":").map((p) => parseInt(p, 10));
  if (parts.length !== 2 || parts.some(Number.isNaN)) return null;
  return parts[0] * 60 + parts[1];
}

export default function ClipReadyPanel({ review, onConfirmed }) {
  const [busy, setBusy] = useState(null);
  const [adjusting, setAdjusting] = useState(false);
  const [adjStart, setAdjStart] = useState("");
  const [adjEnd, setAdjEnd] = useState("");

  const segs = review.clip_segments || [];
  const conf = review.clip_confidence || "low";
  const hasClip = !!review.generated_clip_path && conf !== "low";

  const totalDur = segs.reduce(
    (a, s) => a + Math.max(0, (s.end_seconds || 0) - (s.start_seconds || 0)),
    0
  );

  const callConfirm = async (action, body = {}) => {
    if (!MEDIA_FEATURE_ENABLED) return;       // Quarantine — no POST.
    setBusy(action);
    try {
      await axios.post(
        `${API}/api/lesson-review/${review.id}/clip-confirm`,
        { action, ...body },
        { withCredentials: true }
      );
      toast.success("Grading started — analysis will appear shortly.");
      onConfirmed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm clip");
    } finally {
      setBusy(null);
    }
  };

  const onAdjust = () => {
    const s = mmssToSeconds(adjStart);
    const e = mmssToSeconds(adjEnd);
    if (s == null || e == null) return toast.error("Use MM:SS, e.g. 12:10");
    if (e <= s) return toast.error("End must be after start");
    callConfirm("adjust", { start_seconds: s, end_seconds: e });
  };

  // Media quarantine — silent. No retry/grade CTAs surface while
  // /api/lesson-review/* is quarantined.
  if (!MEDIA_FEATURE_ENABLED) return null;

  return (
    <div
      data-testid="clip-ready-panel"
      className="rounded-2xl border border-indigo-700/40 bg-gradient-to-br from-indigo-900/30 to-slate-900/30 p-4 mt-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-indigo-300" />
        <p className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-300">
          Auto-extracted clip ready
        </p>
        <span
          data-testid="clip-confidence"
          className={`ml-auto text-[10px] font-extrabold uppercase tracking-wider rounded-full px-2 py-0.5 border ${CONFIDENCE_TONE[conf] || CONFIDENCE_TONE.low}`}
        >
          {conf} confidence
        </span>
      </div>

      <p className="text-sm text-white font-extrabold leading-snug">
        {hasClip
          ? `We found ${segs.length} relevant moment${segs.length !== 1 ? "s" : ""} (${fmtMmss(totalDur)} total).`
          : "No clear match found — using selected range."}
      </p>

      {hasClip && (
        <div className="mt-3 rounded-xl bg-slate-900/60 border border-slate-800 overflow-hidden">
          <video
            data-testid="clip-preview-video"
            src={`${API}/api/lesson-review/${review.id}/clip`}
            controls
            preload="metadata"
            className="w-full aspect-video bg-black"
          />
        </div>
      )}

      {segs.length > 0 && (
        <ul className="mt-3 space-y-1">
          {segs.map((s, i) => (
            <li
              key={i}
              data-testid={`clip-segment-${i}`}
              className="text-[11px] text-slate-300 flex items-center gap-2"
            >
              <Play className="w-3 h-3 text-indigo-300" />
              <span className="font-extrabold">
                {fmtMmss(s.start_seconds)} → {fmtMmss(s.end_seconds)}
              </span>
              {(s.matched_phrases || []).slice(0, 3).map((m, j) => (
                <span
                  key={j}
                  className="px-1.5 py-0.5 rounded bg-slate-800 text-[10px] text-slate-400"
                >
                  {m}
                </span>
              ))}
            </li>
          ))}
        </ul>
      )}

      {!adjusting && (
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          {hasClip && (
            <button
              data-testid="clip-use"
              onClick={() => callConfirm("use_clip")}
              disabled={!!busy}
              className="flex-1 h-11 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white font-extrabold text-sm flex items-center justify-center gap-2"
            >
              {busy === "use_clip" ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</>
              ) : (
                <>Use this clip</>
              )}
            </button>
          )}
          <button
            data-testid="clip-adjust"
            onClick={() => setAdjusting(true)}
            disabled={!!busy}
            className="flex-1 h-11 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 font-extrabold text-sm flex items-center justify-center gap-2"
          >
            <Wand2 className="w-4 h-4" /> Adjust clip
          </button>
          <button
            data-testid="clip-use-original"
            onClick={() => callConfirm("use_original")}
            disabled={!!busy}
            className="flex-1 h-11 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 font-extrabold text-sm flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-4 h-4" /> Use original range
          </button>
        </div>
      )}

      {adjusting && (
        <div
          data-testid="clip-adjust-form"
          className="mt-4 rounded-xl bg-slate-900/60 border border-slate-800 p-3 space-y-3"
        >
          <p className="text-[11px] text-slate-400">
            Trim window (MM:SS):
          </p>
          <div className="grid grid-cols-2 gap-2">
            <input
              data-testid="clip-adjust-start"
              type="text"
              inputMode="numeric"
              placeholder="12:10"
              value={adjStart}
              onChange={(e) => setAdjStart(e.target.value)}
              className="rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white"
            />
            <input
              data-testid="clip-adjust-end"
              type="text"
              inputMode="numeric"
              placeholder="14:30"
              value={adjEnd}
              onChange={(e) => setAdjEnd(e.target.value)}
              className="rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white"
            />
          </div>
          <div className="flex gap-2">
            <button
              data-testid="clip-adjust-cancel"
              onClick={() => setAdjusting(false)}
              disabled={!!busy}
              className="flex-1 h-10 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-extrabold"
            >
              Cancel
            </button>
            <button
              data-testid="clip-adjust-apply"
              onClick={onAdjust}
              disabled={!!busy}
              className="flex-[2] h-10 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white text-sm font-extrabold flex items-center justify-center gap-2"
            >
              {busy === "adjust" ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</>
              ) : (
                <>Apply trim</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
